import { faMicrophone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, DatePicker } from '@repo/videa-components';
import { useAtom, useSetAtom } from 'jotai';
import { useSelector } from 'react-redux';

import { useGetDateFNSLocale } from '@/locales/helpers';
import { VoiceNotesSource } from '@/modules/videaNotes/types';
import { usePreferences } from '@/react-query/usePreferences';

import {
  operatoryFilterAtom,
  selectDateAtom,
  selectedProvidersAtom,
} from '../../../../modules/patientList/atoms';
import { PatientListPageLayoutContextMap } from '../../../../modules/patientList/types';
import {
  VideaNotesUIState,
  useOpenVideaNotes,
} from '../../../../modules/videaNotes/helpers';
import { PatientSearch } from '../PatientSearch';

export const AmbientTopbarContent = () => {
  const { t } = useLingui();
  const dateLocale = useGetDateFNSLocale();

  const [date, setDate] = useAtom(selectDateAtom);
  const user = useSelector((state: any) => state?.login?.user);

  const setSelectedOperatories = useSetAtom(operatoryFilterAtom);
  const setSelectedProviders = useSetAtom(selectedProvidersAtom);
  const openVideaNotes = useOpenVideaNotes();

  const { data: prefs } = usePreferences();

  const handleSelectDate = (newDate: Date) => {
    setDate(newDate);
    setSelectedOperatories([]);
    setSelectedProviders([]);
  };

  const handleStartNote = () => {
    // Create a minimal appointment data for quick note (no existing appointment)
    const mockAppointment = {
      appointment: {
        unifiedId: '', // Empty string for new notes
        start: (date || new Date()).toISOString(),
        estimatedVisitId: null, // No existing visit
      },
      patient: {
        firstName: 'Quick',
        lastName: 'Note',
        displayName: 'Quick Note',
      },
      lastTreatments: {
        lastBw: null,
        lastFluoride: null,
        lastFmx: null,
        lastPerioChart: null,
        lastPerioMaintenance: null,
        lastProphy: null,
        lastSrp: null,
      },
    };

    openVideaNotes(
      user,
      mockAppointment,
      VideaNotesUIState.PATIENT_INFO,
      VoiceNotesSource.QUICK_NOTE,
    );
  };

  return (
    <div className="vai-flex vai-items-center vai-justify-between vai-w-full">
      <div className="vai-flex vai-items-center vai-gap-4">
        {/* Date Picker with Navigation */}
        <DatePicker
          date={date || undefined}
          setDate={handleSelectDate}
          placeholderText={t`Pick a date`}
          locale={dateLocale}
          showPreviousNextButtons
          showTodayButton
          todayButtonText={t`Back to Today`}
        />

        {/* Search Bar */}
        <div className="vai-w-80">
          <PatientSearch context={PatientListPageLayoutContextMap.Ambient} />
        </div>
      </div>

      {/* Start Note Button - Right justified */}
      {prefs?.enableQuickNotes && (
        <Button
          onClick={handleStartNote}
          className="vai-bg-brand-primary hover:vai-bg-brand-primary/90 vai-text-white hover:vai-text-white vai-border-none vai-flex vai-items-center vai-gap-2"
        >
          <FontAwesomeIcon icon={faMicrophone} className="vai-h-4 vai-w-4" />
          <Trans>Start Note</Trans>
        </Button>
      )}
    </div>
  );
};
